import { FilterGroupOption } from "@components/common/data-grid/adm-data-grid-types";
import {
  DESELECT_ALL_VALUE,
  renderBulkActions,
  SELECT_ALL_VALUE,
} from "@components/common/data-grid/toolbar/filter-bulk-actions";
import { Chip, FontWeights, grey, Text } from "@flat-ui";
import {
  Divider,
  FormControl,
  ListSubheader,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { Box, Stack } from "@mui-wrapper";
// eslint-disable-next-line no-restricted-imports -- A multi-select needs MUI's MenuItem, which flat-ui does not wrap
import { MenuItem } from "@mui/material";

/**
 * Chips shown for the selection, the rest is summarized as a counter. Without a limit the chips
 * wrap onto new lines and the dropdown, and everything below it, moves with every option picked.
 */
const MAX_VISIBLE_CHIPS = 3;

/** Top margin of a group heading that has a divider above it, as used by flat-ui's own dropdown */
const HEADING_MARGIN_TOP_WITH_DIVIDER = 1.5;

/** Height the options start scrolling at, so a long list does not run over the whole page */
const MAX_MENU_HEIGHT = "45rem";

interface Props {
  /** Label shown while nothing is selected, replaced by the chips afterwards */
  label: string;

  /** The options offered inside the dropdown */
  options: FilterGroupOption[];

  /** Ids of the options currently selected */
  selectedIds: string[];

  /** Called with the next selection whenever it changes */
  onChange: (selectedIds: string[]) => void;

  /** Whether to offer the actions that select every option at once, or deselect them all again */
  hasSelectAll?: boolean;
}

/**
 * @param option the option to list
 * @returns the option as a chip when it carries a color, as plain text otherwise
 */
function renderOptionLabel({
  label,
  color,
  backgroundColor,
}: FilterGroupOption): React.JSX.Element {
  if (!backgroundColor) {
    return <Text variant="bodyM">{label}</Text>;
  }

  return (
    <Chip
      label={label}
      color={color}
      backgroundColor={backgroundColor}
      textTransform="none"
    />
  );
}

/**
 * Builds the menu items as plain elements rather than a component, because the select wires the
 * selection up by cloning its direct children.
 *
 * @param options every option of the dropdown, those of a group next to each other
 * @returns the menu items, each group headed the way the Add Subscription page heads its sections
 */
function renderOptions(options: FilterGroupOption[]): React.JSX.Element[] {
  return options.flatMap((option, index) => {
    const menuItem = (
      <MenuItem key={option.id} value={option.id} sx={{ px: 1.5 }}>
        <Stack sx={{ alignItems: "flex-start" }}>
          {renderOptionLabel(option)}
          {option.subtitle ? (
            <Text variant="bodyS" sx={{ color: grey[600] }}>
              {option.subtitle}
            </Text>
          ) : null}
        </Stack>
      </MenuItem>
    );

    const { groupLabel } = option;
    const isGroupStart =
      groupLabel !== undefined && groupLabel !== options[index - 1]?.groupLabel;

    if (!isGroupStart) {
      return menuItem;
    }

    return [
      // A subheader carries no tabindex, so the select ignores clicks on it
      <ListSubheader
        key={`group-of-${option.id}`}
        // Sticky headings would pile up on each other, as this one carries no background of its own
        disableSticky
        sx={{ backgroundColor: "transparent", p: 0 }}
      >
        {index > 0 && <Divider />}
        <Text
          component="div"
          variant="bodyM"
          sx={{
            color: grey[800],
            fontSize: "0.75rem",
            textTransform: "uppercase",
            fontWeight: FontWeights.Bold,
            mx: 1.5,
            mt: index === 0 ? 1 : HEADING_MARGIN_TOP_WITH_DIVIDER,
            mb: 1.5,
          }}
        >
          {groupLabel}
        </Text>
      </ListSubheader>,
      menuItem,
    ];
  });
}

/**
 * A multi-select dropdown that shows a placeholder until options are selected, then replaces it with
 * the same removable, colored chips the plans table uses.
 *
 * @returns the dropdown for one filter group
 */
export function FilterDropdown({
  label,
  options,
  selectedIds,
  onChange,
  hasSelectAll = false,
}: Props): React.JSX.Element {
  function optionOf(optionId: string): FilterGroupOption | undefined {
    return options.find((option) => option.id === optionId);
  }

  const hasBulkActions = hasSelectAll && options.length > 0;

  function handleChange(event: SelectChangeEvent<string[]>): void {
    const { value } = event.target;
    const nextIds = typeof value === "string" ? value.split(",") : value;

    if (nextIds.includes(SELECT_ALL_VALUE)) {
      onChange(options.map(({ id }) => id));
      return;
    }

    if (nextIds.includes(DESELECT_ALL_VALUE)) {
      onChange([]);
      return;
    }

    onChange(nextIds);
  }

  function removeOption(optionId: string): void {
    onChange(selectedIds.filter((selectedId) => selectedId !== optionId));
  }

  const hasSelection = selectedIds.length > 0;
  const visibleIds = selectedIds.slice(0, MAX_VISIBLE_CHIPS);
  const hiddenCount = selectedIds.length - visibleIds.length;

  return (
    <FormControl size="small" sx={{ minWidth: "9rem" }}>
      <Select
        multiple
        displayEmpty
        value={selectedIds}
        onChange={handleChange}
        inputProps={{ "aria-label": label }}
        MenuProps={{
          // The menu is wider than the select, and MUI centers it, which drifts it over the
          // neighboring filter. Anchored left it grows away from that filter instead.
          anchorOrigin: { vertical: "bottom", horizontal: "left" },
          transformOrigin: { vertical: "top", horizontal: "left" },
          slotProps: { paper: { sx: { maxHeight: MAX_MENU_HEIGHT } } },
        }}
        renderValue={() =>
          hasSelection ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {visibleIds.map((optionId) => {
                const option = optionOf(optionId);
                return (
                  // Stop the mouse down from bubbling up so removing a chip does not open the menu
                  <Box
                    key={optionId}
                    component="span"
                    onMouseDown={(event) => event.stopPropagation()}
                  >
                    <Chip
                      label={option?.label ?? optionId}
                      color={option?.color}
                      backgroundColor={option?.backgroundColor ?? grey[300]}
                      textTransform="none"
                      onDelete={() => removeOption(optionId)}
                    />
                  </Box>
                );
              })}

              {hiddenCount > 0 && (
                <Text
                  variant="bodyM"
                  sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
                >
                  +{hiddenCount}
                </Text>
              )}
            </Box>
          ) : (
            <Text variant="bodyM" sx={{ color: "text.secondary" }}>
              {label}
            </Text>
          )
        }
      >
        {hasBulkActions && renderBulkActions({ options, selectedIds })}

        {renderOptions(options)}
      </Select>
    </FormControl>
  );
}
